import { useState } from "react";

/**
 * HelpForm — the subject and the comment of the help screen.
 *
 * Props:
 *   onSubmit({ subject, body }) — called only when both fields pass validation
 *   submitting — disables the button while the request is in flight, so a
 *     second click cannot put a second message in the support mailbox
 *
 * Validation is here and on the server, on purpose: this copy is what turns a
 * forgotten field into a sentence next to the field, and the server's copy is
 * what holds when the request did not come from this screen. Neither replaces
 * the other, so the caps below are the same numbers the endpoint enforces —
 * when they drift, the screen promises a limit the API then refuses.
 */

/** Characters, not bytes, matching maxContactSubjectRunes / maxContactBodyRunes. */
export const HELP_SUBJECT_MAX = 200;
export const HELP_BODY_MAX = 5000;

export function validateHelpForm(subject, body) {
  const errors = {};
  const trimmedSubject = subject.trim();
  const trimmedBody = body.trim();

  if (!trimmedSubject) {
    errors.subject = "Add a subject so support knows what this is about.";
  } else if (trimmedSubject.length > HELP_SUBJECT_MAX) {
    errors.subject = `Keep the subject to ${HELP_SUBJECT_MAX} characters or fewer.`;
  }

  if (!trimmedBody) {
    errors.body = "Write a message before sending.";
  } else if (trimmedBody.length > HELP_BODY_MAX) {
    errors.body = `Keep the message to ${HELP_BODY_MAX} characters or fewer.`;
  }

  return errors;
}

const labelStyle = {
  fontSize: "var(--text-sm)",
  color: "var(--text-secondary)",
  display: "block",
  marginBottom: 4,
};

const fieldErrorStyle = {
  fontSize: "var(--text-xs)",
  color: "var(--text-danger)",
  margin: "4px 0 0",
};

export default function HelpForm({ onSubmit, submitting = false }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [errors, setErrors] = useState({});

  function handleSubmit(event) {
    event.preventDefault();
    const found = validateHelpForm(subject, body);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    onSubmit?.({ subject: subject.trim(), body: body.trim() });
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <label htmlFor="help-subject" style={labelStyle}>
        Subject
      </label>
      <input
        id="help-subject"
        className="ical-input"
        type="text"
        maxLength={HELP_SUBJECT_MAX}
        placeholder="A one-line summary of the problem"
        style={{ width: "100%", borderColor: errors.subject ? "var(--fill-danger)" : undefined }}
        value={subject}
        aria-invalid={errors.subject ? "true" : "false"}
        aria-describedby={errors.subject ? "help-subject-error" : undefined}
        onChange={(event) => {
          setSubject(event.target.value);
          // Clearing the message as soon as the field is touched keeps the
          // form from arguing with a value the customer has already changed.
          if (errors.subject) setErrors((prev) => ({ ...prev, subject: undefined }));
        }}
      />
      {errors.subject ? (
        <p id="help-subject-error" role="alert" style={fieldErrorStyle}>
          {errors.subject}
        </p>
      ) : null}

      <label htmlFor="help-body" style={{ ...labelStyle, marginTop: 12 }}>
        Comment
      </label>
      <textarea
        id="help-body"
        className="ical-input"
        rows={7}
        maxLength={HELP_BODY_MAX}
        placeholder="What happened, and what did you expect instead?"
        style={{
          width: "100%",
          resize: "vertical",
          borderColor: errors.body ? "var(--fill-danger)" : undefined,
        }}
        value={body}
        aria-invalid={errors.body ? "true" : "false"}
        aria-describedby={errors.body ? "help-body-error" : undefined}
        onChange={(event) => {
          setBody(event.target.value);
          if (errors.body) setErrors((prev) => ({ ...prev, body: undefined }));
        }}
      />
      {errors.body ? (
        <p id="help-body-error" role="alert" style={fieldErrorStyle}>
          {errors.body}
        </p>
      ) : null}

      <button
        type="submit"
        className="ical-btn ical-btn-primary"
        style={{ marginTop: 16 }}
        disabled={submitting}
      >
        {submitting ? "Sending…" : "Send message"}
      </button>
    </form>
  );
}
